/* Yatas Bedding NL — tab switching for the homepage product rails.

   Speed (2026-09-11): only the first tab of each rail is rendered on the
   server. Every other panel arrives without cards: it carries data-lazy-src
   (its collection URL) and a <template data-lazy-shell> holding the rail's
   carousel markup. The first time a tab is shown, its cards are fetched through
   the Section Rendering API ({collection}?section_id=ybn-rail-panel), put into a
   copy of the shell, and inserted. The request starts early on hover, focus or
   touch, so switching tabs still feels instant.

   Before this, 15 hidden panels (~140 cards, 600+ images) sat in the HTML of
   every homepage view - about 85% of the page's DOM. */
(function () {
  'use strict';

  const pending = new Map();

  function load(panel) {
    const src = panel.dataset.lazySrc;
    if (!pending.has(src)) {
      const url = src + (src.indexOf('?') === -1 ? '?' : '&') + 'section_id=ybn-rail-panel';
      const request = fetch(url, { credentials: 'same-origin' })
        .then((res) => {
          if (!res.ok) throw new Error('rail panel ' + res.status);
          return res.text();
        })
        .catch((err) => {
          pending.delete(src); // allow a retry on the next reveal
          throw err;
        });
      pending.set(src, request);
    }
    return pending.get(src);
  }

  function fill(panel) {
    if (!panel.dataset.lazySrc || panel.dataset.lazyState) return;
    const shell = panel.querySelector('template[data-lazy-shell]');
    if (!shell) return;

    panel.dataset.lazyState = 'loading';
    panel.setAttribute('aria-busy', 'true');

    load(panel)
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const limit = parseInt(panel.dataset.lazyLimit, 10) || 12;
        const slides = Array.from(doc.querySelectorAll('[data-slide]')).slice(0, limit);
        const frag = shell.content.cloneNode(true);
        const track = frag.querySelector('[data-track]');
        slides.forEach((slide) => track.appendChild(document.importNode(slide, true)));

        // ybn-carousel reads its slides once, when it connects, so it has to be
        // inserted complete rather than filled afterwards.
        shell.replaceWith(frag);
        panel.dataset.lazyState = 'done';
        panel.removeAttribute('aria-busy');
        panel.style.minHeight = '';

        // Card galleries wire themselves up on this event (yatas-cardgallery.js).
        document.dispatchEvent(new CustomEvent('ybn:grid-rendered', { detail: { grid: panel } }));
      })
      .catch(() => {
        // The "Alles bekijken" link stays as the way in; retry on next reveal.
        delete panel.dataset.lazyState;
        panel.removeAttribute('aria-busy');
        panel.style.minHeight = '';
      });
  }

  function init(section) {
    const buttons = Array.from(section.querySelectorAll('[data-tab-target]'));
    if (buttons.length < 2) return;

    const panelFor = (btn) =>
      section.querySelector('[data-tab-panel="' + btn.dataset.tabTarget + '"]');

    buttons.forEach((btn) => {
      // Start the request as soon as intent shows; the click then finds it ready.
      const warm = () => {
        const panel = panelFor(btn);
        if (panel && panel.dataset.lazySrc && !panel.dataset.lazyState) load(panel).catch(() => {});
      };
      btn.addEventListener('pointerenter', warm, { once: true });
      btn.addEventListener('focus', warm, { once: true });
      btn.addEventListener('touchstart', warm, { once: true, passive: true });

      btn.addEventListener('click', () => {
        const id = btn.dataset.tabTarget;

        // Keep the rail's height while an unloaded tab fetches, so the page
        // below does not jump when the cards arrive.
        const current = section.querySelector('[data-tab-panel]:not(.is-hidden)');
        const reserve = current ? current.offsetHeight : 0;

        buttons.forEach((b) => {
          const on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-selected', String(on));
        });

        section.querySelectorAll('[data-tab-panel]').forEach((panel) => {
          const show = panel.dataset.tabPanel === id;
          panel.classList.toggle('is-hidden', !show);
          if (!show) return;

          // A carousel measured while hidden has zero width, so re-measure on
          // reveal.
          panel.querySelectorAll('ybn-carousel').forEach((c) => c.applyPerView?.());

          if (panel.dataset.lazySrc && !panel.dataset.lazyState && reserve) {
            panel.style.minHeight = reserve + 'px';
          }
          fill(panel);
        });
      });
    });
  }

  const start = () => {
    document.querySelectorAll('.ybn-prodrail, .ybn-catrail, .ybn-world').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
